import { FlowBaseUrlEnum } from '@constants/app.constants';
import { TripType } from '@constants/home.constants';
import { BaseError } from '@models/app.models';
import { PaymentForm } from '@models/form/form.models';
import { BoardingPassRequest } from '@models/requests/boarding-pass-request.model';
import { SearchRequest } from '@models/requests/search-request.model';
import { N0SellKey } from '@models/requests/trip-sell-request.model';
import {
  AppleBoardingPassResult,
  GoogleBoardingPassResult,
} from '@models/responses/boarding-pass-result.model';
import { N0SsrAvailability } from '@models/responses/ssr-availability-response.model';
import { Action, createAction, props } from '@ngrx/store';
import { Booking, BookingPaymentResult } from 'src/app/models/booking.models';
import {CreditCardFeeMap, CreditCardType} from "../../../typings/credit-card-type";
import {CorporateCreditCardFeeResponse} from "@models/responses/payment-api-response.model";

export const enum BookingActionTypes {
  storeBooking = '[booking] STORE_BOOKING',
  getBooking = '[booking] GET_BOOKING',
  getBookingSuccess = '[booking] GET_BOOKING_SUCCESS',
  getBookingAfterPassengerUpdateSuccess = '[booking] GET_BOOKING_AFTER_PASSENGER_UPDATE_SUCCESS',
  deleteBooking = '[booking] DELETE_BOOKING',
  deleteBookingSuccess = '[booking] DELETE_BOOKING_SUCCESS',
  deleteBookingNoNavigation = '[booking] DELETE_BOOKING_NO_NAVIGATION',
  commitBooking = '[booking] COMMIT_BOOKING',
  commitBookingSuccess = '[booking] COMMIT_BOOKING_SUCCESS',
  completeBooking = '[booking] COMPLETE_BOOKING',
  completeBookingSuccess = '[booking] COMPLETE_BOOKING_SUCCESS',
  completeBookingPendingCustomerAction = '[booking] COMPLETE_BOOKING_PENDING_CUSTOMER_ACTION',
  upgradeCabin = '[booking] UPGRADE_CABIN',
  updateBooking = '[booking] UPDATE_BOOKING',
  updateBookingSuccess = '[booking] UPDATE_BOOKING_SUCCESS',
  updateBookingAfterPassengerUpdateSuccess = '[booking] UPDATE_BOOKING_AFTER_PASSENGER_UPDATE_SUCCESS',
  getSsrAvailability = '[ssr] GET_AVAILABILITY',
  getSsrAvailabilitySuccess = '[ssr] GET_AVAILABILITY_SUCCESS',
  upgradeToEconomyClassicAction = '[booking] UPGRADE_TO_ECONOMY_CLASSIC',
  upgradeToEconomyClassicActionSuccess = '[booking] UPGRADE_TO_ECONOMY_CLASSIC_SUCCESS',

  retrieveBooking = '[booking] RETRIEVE_BOOKING',
  retrieveBookingNoNavigation = '[booking] RETRIEVE_BOOKING_NO_NAVIGATION',
  retrieveBookingDeeplinkBags = '[booking] RETRIEVE_BOOKING_DEEPLINK_BAGS',
  retrieveBookingDeeplinkSeats = '[booking] RETRIEVE_BOOKING_DEEPLINK_SEATS',
  retrieveBookingDeeplinkMeals = '[booking] RETRIEVE_BOOKING_DEEPLINK_MEALS',
  retrieveBookingDeeplinkExtras = '[booking] RETRIEVE_BOOKING_DEEPLINK_EXTRAS',
  retrieveBookingDeeplinkPay = '[booking] RETRIEVE_BOOKING_DEEPLINK_PAY',
  retrieveBookingSuccess = '[booking] RETRIEVE_BOOKING_SUCCESS',
  retrieveBookingError = '[booking] RETRIEVE_BOOKING_ERROR',
  resetBookingError = '[booking] RESET_BOOKING_ERROR',
  setBookingError = '[booking] SET_BOOKING_ERROR',
  retrieveTokenError = '[booking] RETRIEVE_TOKEN_ERROR',
  retrieveOriginalBooking = '[booking] RETRIEVE_ORIGINAL_BOOKING',
  setOriginalBooking = '[booking] SET_ORIGINAL_BOOKING',
  clearOriginalBooking = '[booking] CLEAR_ORIGINAL_BOOKING',
  addToCalendar = '[booking] ADD_TO_CALENDAR',
  bookingCancelFlights = '[booking] BOOKING_CANCEL_FLIGHTS',
  bookingCancelFlightsSuccess = '[booking] BOOKING_CANCEL_FLIGHTS_SUCCESS',
  bookingViewBoardingPass = '[booking] BOOKING_VIEW_BOARDING_PASS',
  bookingCheckInFlights = '[booking] BOOKING_CHECK_IN_FLIGHTS',
  bookingTravelerChange = '[booking] BOOKING_TRAVELER_CHANGE',
  bookingCheckInFlightsSuccess = '[booking] BOOKING_CHECK_IN_FLIGHTS_SUCCESS',
  bookingCheckPaymentsStatus = '[booking] CHECK_PAYMENTS_STATUS',
  bookingCheckPaymentsStatusSuccess = '[booking] CHECK_PAYMENTS_STATUS_SUCCESS',
  bookingPaymentsAccepted = '[booking] PAYMENTS_ACCEPTED',
  bookingPaymentsAcceptedSuccess = '[booking] PAYMENTS_ACCEPTED_SUCCESS',
  bookingFurtherPaymentRequired = '[booking] FURTHER_PAYMENT_REQUIRED',
  bookingPaymentsAcceptedBookingUpdated = '[booking] PAYMENTS_ACCEPTED_BOOKING_UPDATED',
  bookingPaymentsDeclined = '[booking] PAYMENTS_DECLINED',
  bookingRefundPayments = '[booking] REFUND_PAYMENTS',
  bookingCancelZeroBalance = '[booking] CANCEL_ZERO_BALANCE',
  bookingCancelWithPayment = '[booking] CANCEL_WITH_PAYMENT',
  bookingCancelShowModal = '[booking] CANCEL_SHOW_MODAL',
  bookingCancelWithPaymentPaymentSuccess = '[booking] CANCEL_WITH_PAYMENT_PAYMENT_SUCCESS',
  tripDetailsInit = '[booking] TRIP_DETAILS_INIT',
  bookingManageBooking = '[booking] MANAGE_BOOKING',
  addBags = '[booking] ADD_BAGS',
  viewChangeSeats = '[booking] VIEW_CHANGE_SEATS',
  viewChangeMeals = '[booking] VIEW_CHANGE_MEALS',
  addExtras = '[booking] ADD_EXTRAS',
  dangerousGoodsViewed = '[booking] DANGEROUS_GOODS_VIEWED',
  tripDisruptionCheckSuccess = '[booking] TRIP_DISRUPTION_CHECK_SUCCESS',
  cancelModification = '[booking] CANCEL_MODIFICATION',

  saveGoogleBoardingPassToWallet = '[booking] SAVE_GOOGLE_BOARDING_PASS_TO_WALLET',
  bookingCreateGoogleBoardingPassSuccess = '[booking] GOOGLE_BOARDING_PASS_CREATED',
  saveAppleBoardingPassToWallet = '[booking] SAVE_APPLE_BOARDING_PASS_TO_WALLET',
  bookingCreateAppleBoardingPassSuccess = '[booking] APPLE_BOARDING_PASS_CREATED',

  saveGoogleBoardingPassesToWallet = '[booking] SAVE_GOOGLE_BOARDING_PASSES_TO_WALLET',
  bookingCreateGoogleBoardingPassesSuccess = '[booking] GOOGLE_BOARDING_PASSES_CREATED',
  saveAppleBoardingPassesToWallet = '[booking] SAVE_APPLE_BOARDING_PASSES_TO_WALLET',
  bookingCreateAppleBoardingPassesSuccess = '[booking] APPLE_BOARDING_PASSES_CREATED',

  setTokenGetBooking = '[booking] SET_TOKEN_GET_BOOKING',
  bookingPollBooking = '[booking] POLL_BOOKING',
  loadCreditCardFees = '[booking] LOAD_CC_FEES',
  loadCreditCardFeesSuccess = '[booking] LOAD_CC_FEES_SUCCESS',
  checkCreditCardFee = '[booking] CHECK_CC_FEE',
  addCreditCardFee = '[booking] ADD_CC_FEE',
  checkCreditCardFeeSuccess = '[booking] CHECK_CC_FEE_SUCCESS',
  resetCreditCardFee = '[booking] RESET_CC_FEE',
  resetCreditCardFeeSucceess = '[booking] RESET_CC_FEE_SUCCESS',
}

export const bookingCreateGoogleBoardingPassSuccess = createAction(
  BookingActionTypes.bookingCreateGoogleBoardingPassSuccess,
  props<{
    boardingPassRequest: BoardingPassRequest;
    boardingPassResponse: GoogleBoardingPassResult;
  }>(),
);

export const bookingCreateAppleBoardingPassSuccess = createAction(
  BookingActionTypes.bookingCreateAppleBoardingPassSuccess,
  props<{
    boardingPassRequest: BoardingPassRequest;
    boardingPassResponse: AppleBoardingPassResult;
  }>(),
);

export const saveGoogleBoardingPassToWallet = createAction(
  BookingActionTypes.saveGoogleBoardingPassToWallet,
  props<{ boardingPassRequest: BoardingPassRequest }>(),
);

export const saveAppleBoardingPassToWallet = createAction(
  BookingActionTypes.saveAppleBoardingPassToWallet,
  props<{ boardingPassRequest: BoardingPassRequest }>(),
);

export const bookingCreateGoogleBoardingPassesSuccess = createAction(
  BookingActionTypes.bookingCreateGoogleBoardingPassesSuccess,
  props<{
    boardingPassRequests: BoardingPassRequest[];
    boardingPassResponse: GoogleBoardingPassResult;
  }>(),
);

export const bookingCreateAppleBoardingPassesSuccess = createAction(
  BookingActionTypes.bookingCreateAppleBoardingPassesSuccess,
  props<{
    boardingPassRequests: BoardingPassRequest[];
    boardingPassResponse: AppleBoardingPassResult;
  }>(),
);

export const saveGoogleBoardingPassesToWallet = createAction(
  BookingActionTypes.saveGoogleBoardingPassesToWallet,
  props<{ boardingPassRequests: BoardingPassRequest[] }>(),
);

export const saveAppleBoardingPassesToWallet = createAction(
  BookingActionTypes.saveAppleBoardingPassesToWallet,
  props<{
    boardingPassRequests: BoardingPassRequest[];
  }>(),
);

export const bookingPaymentsDeclined = createAction(
  BookingActionTypes.bookingPaymentsDeclined,
  props<{ error: BaseError }>(),
);

export const bookingPaymentsAccepted = createAction(
  BookingActionTypes.bookingPaymentsAccepted
);

export const bookingPaymentsAcceptedSuccess = createAction(
  BookingActionTypes.bookingPaymentsAcceptedSuccess,
);

export const bookingFurtherPaymentRequired = createAction(
  BookingActionTypes.bookingFurtherPaymentRequired,
);

export const bookingPaymentsAcceptedBookingUpdated = createAction(
  BookingActionTypes.bookingPaymentsAcceptedBookingUpdated,
  props<{ booking: Booking }>(),
);

export const bookingCheckPaymentsStatus = createAction(
  BookingActionTypes.bookingCheckPaymentsStatus,
);

export const bookingCheckPaymentsStatusSuccess = createAction(
  BookingActionTypes.bookingCheckPaymentsStatusSuccess,
  props<{
    data: BookingPaymentResult;
    triggeredByAction?: string;
  }>(),
);

export const bookingStoreBooking = createAction(
  BookingActionTypes.storeBooking,
  props<{ payload: Booking }>(),
);

export const bookingGetBooking = createAction(
  BookingActionTypes.getBooking,
  props<{ next?: Action | Action[], doccheck?: boolean, flow?: FlowBaseUrlEnum }>(),
);

export const bookingGetBookingSuccess = createAction(
  BookingActionTypes.getBookingSuccess,
  props<{ booking: Booking; navigate?: boolean }>(),
);

export const bookingGetBookingAfterPassengerUpdateSuccess = createAction(
  BookingActionTypes.getBookingAfterPassengerUpdateSuccess,
  props<{ booking: Booking, navigate?: boolean }>(),
);

export const bookingDeleteBooking = createAction(
  BookingActionTypes.deleteBooking,
);

export const bookingDeleteBookingSuccess = createAction(
  BookingActionTypes.deleteBookingSuccess,
);

export const bookingDeleteBookingNoNavigation = createAction(
  BookingActionTypes.deleteBookingNoNavigation,
);

export const bookingCommitBooking = createAction(
  BookingActionTypes.commitBooking,
);

export const bookingCommitBookingSuccess = createAction(
  BookingActionTypes.commitBookingSuccess,
);

export const bookingCompleteBooking = createAction(
  BookingActionTypes.completeBooking,
);

export const bookingCompleteBookingSuccess = createAction(
  BookingActionTypes.completeBookingSuccess,
  props<{
    data: BookingPaymentResult;
    triggeredByAction?: string;
  }>(),
);

export const bookingCompleteBookingPendingCustomerAction = createAction(
  BookingActionTypes.completeBookingPendingCustomerAction,
  props<{ redirectUrl: string }>(),
);

export const bookingUpdateBooking = createAction(
  BookingActionTypes.updateBooking,
);

export const bookingUpdateBookingSuccess = createAction(
  BookingActionTypes.updateBookingSuccess,
  props<{
    data: BookingPaymentResult;
    triggeredByAction?: string;
  }>(),
);

export const bookingUpdateBookingAfterPassengerUpdateSuccess = createAction(
  BookingActionTypes.updateBookingAfterPassengerUpdateSuccess,
  props<{
    data: BookingPaymentResult;
    triggeredByAction?: string;
  }>(),
);

export const bookingGetSsrAvailability = createAction(
  BookingActionTypes.getSsrAvailability,
);

export const bookingGetSsrAvailabilitySuccess = createAction(
  BookingActionTypes.getSsrAvailabilitySuccess,
  props<{ payload: N0SsrAvailability }>(),
);

export const upgradeToEconomyClassicAction = createAction(
  BookingActionTypes.upgradeToEconomyClassicAction,
  props<{
    redirect?: boolean
  }>(),
);

export const upgradeToEconomyClassicActionSuccess = createAction(
  BookingActionTypes.upgradeToEconomyClassicActionSuccess,
  props<{
    sellKeys: N0SellKey[];
    redirect?: boolean;
  }>(),
);

export const bookingRetrieveBooking = createAction(
  BookingActionTypes.retrieveBooking,
  props<{
    confirmationNumber: string;
    lastName: string;
    next?: Action;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveTokenError = createAction(
  BookingActionTypes.retrieveTokenError,
  props<{ next: Action }>(),
);

export const bookingRetrieveBookingNoNavigation = createAction(
  BookingActionTypes.retrieveBookingNoNavigation,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveBookingDeeplinkBags = createAction(
  BookingActionTypes.retrieveBookingDeeplinkBags,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveBookingDeeplinkSeats = createAction(
  BookingActionTypes.retrieveBookingDeeplinkSeats,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveBookingDeeplinkMeals = createAction(
  BookingActionTypes.retrieveBookingDeeplinkMeals,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveBookingDeeplinkExtras = createAction(
  BookingActionTypes.retrieveBookingDeeplinkExtras,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveBookingDeeplinkPay = createAction(
  BookingActionTypes.retrieveBookingDeeplinkPay,
  props<{
    confirmationNumber: string;
    lastName: string;
    fromError?: boolean;
  }>(),
);

export const bookingRetrieveOriginalBooking = createAction(
  BookingActionTypes.retrieveOriginalBooking,
  props<{ confirmationNumber: string; lastName: string }>(),
);

export const bookingRetrieveBookingSuccess = createAction(
  BookingActionTypes.retrieveBookingSuccess,
  props<{ booking: Booking; next?: Action }>(),
);

export const bookingRetrieveBookingError = createAction(
  BookingActionTypes.retrieveBookingError,
);

export const resetBookingError = createAction(
  BookingActionTypes.resetBookingError,
);

export const setBookingError = createAction(
BookingActionTypes.setBookingError,
);

export const bookingSetOriginalBooking = createAction(
  BookingActionTypes.setOriginalBooking,
  props<{ booking: Booking }>(),
);
export const bookingClearOriginalBooking = createAction(
  BookingActionTypes.clearOriginalBooking,
);

export const bookingAddToCalendar = createAction(
  BookingActionTypes.addToCalendar,
);

export const bookingManageBooking = createAction(
  BookingActionTypes.bookingManageBooking,
  props<{
    isTravelersStart?: boolean;
  }>(),
);

export const bookingAddBags = createAction(BookingActionTypes.addBags);

export const bookingViewChangeSeats = createAction(
  BookingActionTypes.viewChangeSeats,
);

export const bookingViewChangeMeals = createAction(
  BookingActionTypes.viewChangeMeals,
);

export const bookingAddExtras = createAction(BookingActionTypes.addExtras);

export const bookingUpgradeCabin = createAction(
  BookingActionTypes.upgradeCabin,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    clearBooking?: boolean;
    compressedFlow?: boolean;
  }>(),
);

export const bookingCancelFlights = createAction(
  BookingActionTypes.bookingCancelFlights,
  props<{ journeyKeys: string[] }>(),
);

export const bookingCancelFlightsSuccess = createAction(
  BookingActionTypes.bookingCancelFlightsSuccess,
  props<{ booking: Booking }>(),
);

export const bookingCheckInFlights = createAction(
  BookingActionTypes.bookingCheckInFlights,
  props<{ checkInPassengers: { [passengerKey: string]: boolean } }>(),
);

export const bookingTravelerChange = createAction(
  BookingActionTypes.bookingTravelerChange,
  props<{
    travelersToChange: { [passengerKey: string]: boolean };
    isTravelersStart?: boolean;
  }>(),
);

export const bookingViewBoardingPass = createAction(
  BookingActionTypes.bookingViewBoardingPass,
  props<{ checkInPassengers: { [passengerKey: string]: boolean } }>(),
);

export const bookingCancelShowModal = createAction(
  BookingActionTypes.bookingCancelShowModal,
  props<{ next: Action }>(),
);

export const bookingRefundPayments = createAction(
  BookingActionTypes.bookingRefundPayments,
);

export const bookingCancelWithPayment = createAction(
  BookingActionTypes.bookingCancelWithPayment,
  props<{
    payments: PaymentForm[];
  }>(),
);

export const bookingCancelWithPaymentPaymentSuccess = createAction(
  BookingActionTypes.bookingCancelWithPaymentPaymentSuccess,
);

export const bookingCancelZeroBalance = createAction(
  BookingActionTypes.bookingCancelZeroBalance,
);

export const bookingTripDetailsInit = createAction(
  BookingActionTypes.tripDetailsInit,
  props<{ lastName: string; confirmationNumber: string, isAPG: boolean }>(),
);

export const bookingDangerousGoodsViewed = createAction(
  BookingActionTypes.dangerousGoodsViewed,
  props<{ payload: { accepted: boolean } }>(),
);

export const bookingSetTokenGetBooking = createAction(
  BookingActionTypes.setTokenGetBooking,
  props<{
    getBooking: boolean;
  }>(),
);

export const tripDisruptionCheckSuccess = createAction(
  BookingActionTypes.tripDisruptionCheckSuccess,
  props<{
    isDisrupted: boolean;
    disruptionUrl: string | undefined;
  }>(),
);

export const bookingCancelModification = createAction(
  BookingActionTypes.cancelModification,
);

export const loadCreditCardFees = createAction(
  BookingActionTypes.loadCreditCardFees,
);

export const loadCreditCardFeesSucceed = createAction(
  BookingActionTypes.loadCreditCardFeesSuccess,
  props<{
    fees: CreditCardFeeMap
  }>(),
);

export const checkCreditCardFee = createAction(
  BookingActionTypes.checkCreditCardFee,
  props<{
    cardBin: string,
  }>(),
);

export const addCreditCardFee = createAction(
  BookingActionTypes.addCreditCardFee,
  props<{
    cardBin: string,
  }>(),
);

export const resetCreditCardFee = createAction(
  BookingActionTypes.resetCreditCardFee,
);

export const checkCreditCardFeeSucceed = createAction(
  BookingActionTypes.checkCreditCardFeeSuccess,
  props<{ response: CorporateCreditCardFeeResponse}>(),
);

export const resetCreditCardFeeSucceed = createAction(
  BookingActionTypes.resetCreditCardFeeSucceess,
);
